import Swal from "sweetalert2";

/**
 * Displays a SweetAlert2 toast notification or modal depending on type.
 * @param {string} message - Message to display.
 * @param {"success"|"error"|"warning"|"info"|"question"} [type="info"] - Alert type.
 * @param {object} [options={}] - Extra SweetAlert2 options (optional).
 */
export async function showAlert(message, type = "info", options = {}) {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 1500, // Adjusted to 1.5 seconds for better readability
    timerProgressBar: true,
    customClass: {
      popup: "rounded-lg",
    },
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
  });

  return Toast.fire({
    icon: type,
    title: message,
    ...options,
  });
}

/**
 * Displays a confirmation modal using SweetAlert2.
 * @param {string} title - Confirmation title.
 * @param {string} text - Description of the action.
 * @param {object} [options={}] - Extra options for customization.
 * @returns {Promise<SweetAlertResult>} - Returns SweetAlert2 result object.
 */
export async function showConfirm(title, text, options = {}) {
  return Swal.fire({
    title,
    html: text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444", // Tailwind red-500
    cancelButtonColor: "#6b7280", // Tailwind gray-500
    confirmButtonText: "Confirmar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    allowOutsideClick: false,
    allowEscapeKey: false,
    ...options,
  });
}

/**
 * Shows validation errors or a SweetAlert2 error modal.
 * Handles Laravel 422 responses and adapts DOM to Tailwind CSS.
 */
export async function handleRequestError(error, defaultMessage) {
  // Clear previous validation errors (Tailwind adaptation)
  document.querySelectorAll(".is-invalid").forEach((el) => {
    el.classList.remove(
      "is-invalid",
      "border-red-500",
      "ring-red-500",
      "focus:ring-red-500",
      "focus:border-red-500",
    );
  });
  document.querySelectorAll(".invalid-feedback").forEach((el) => el.remove());

  // Handle Laravel validation errors (422 Unprocessable Entity)
  if (error.response && error.response.status === 422) {
    const { errors } = error.response.data;

    Object.entries(errors).forEach(([field, messages]) => {
      // Select the input by its 'name' attribute
      const input = document.querySelector(`[name="${field}"]`);
      if (input) {
        // Add Tailwind error classes
        input.classList.add(
          "is-invalid",
          "border-red-500",
          "ring-red-500",
          "focus:ring-red-500",
          "focus:border-red-500",
        );

        // Create and inject the feedback text below the input
        const feedback = document.createElement("p");
        feedback.classList.add(
          "invalid-feedback",
          "mt-1",
          "text-sm",
          "text-red-600",
        );
        feedback.innerHTML = messages.join("<br>");
        input.insertAdjacentElement("afterend", feedback);
      }
    });

    // Show summarized validation alert
    Swal.fire({
      icon: "warning",
      title: "Validação",
      html: "Verifique os campos destacados e tente novamente.",
      confirmButtonColor: "#4f46e5", // Tailwind indigo-600
      confirmButtonText: "Ok",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    return;
  }

  // Generic error (non-422, like 401 Unauthorized or 500 Server Error)
  Swal.fire({
    icon: "error",
    title: "Erro",
    // Tries to get the specific message from the API, falls back to default
    text:
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      defaultMessage,
    confirmButtonColor: "#4f46e5",
    confirmButtonText: "Fechar",
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
}
